import {ChangeDetectorRef, Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild, ViewEncapsulation} from '@angular/core';
import {RuleRoutingActionsService} from '../services/rule-routing-actions.service';
import {MatAccordion} from '@angular/material/expansion';
import * as _ from 'lodash';
import {TopologyElementPrefix, TopologyElementType} from '../../rules.component';
import {ChooseTopologyElementFromMapService} from '../../../../utils/choose-topology-item/services/choose-topology-element-from-map/choose-topology-element-from-map.service';
import {MatRadioChange} from '@angular/material/radio';
import {MatDialog} from '@angular/material/dialog';
import {InformationDialogComponent} from '../../../../dialogs/information-dialog/information-dialog.component';
import {FormControl} from '@angular/forms';
import {ErrorStateMatcher} from '@angular/material/core';

class InputErrorStateMatcher implements ErrorStateMatcher {
  constructor(private showError: boolean) {}
  isErrorState(control: FormControl|null, form): boolean {
    return this.showError;
  }
}

@Component({
  selector: 'routing-rule-dialog-routing-actions',
  templateUrl: './routing-rule-dialog-routing-actions.component.html',
  styleUrls: ['./routing-rule-dialog-routing-actions.component.less'],
  encapsulation: ViewEncapsulation.None
})
export class RoutingRuleDialogRoutingActionsComponent implements OnInit, OnChanges {
  @ViewChild(MatAccordion) routingRuleActions: MatAccordion;

  @Input() rule: any;
  @Input() adminState: string;
  @Input() externalData: any;
  @Input() isValid: any;

  routingMethodOptions = [
    {value: 'seq', label: 'Sequence'},
    {value: 'fork', label: 'Forking'}
  ];

  normalizationGroup: any;
  sipManipulationGroups: any;
  completeNetwork: any;
  completeNetworkWithOffline: any;
  nodesOnlyWithoutThirdPartyWithOffline: any;
  nodesOnlyWithoutThirdParty: any;
  combinedOptions: any = [];
  mapOptions;

  focusedSubAction;
  errorStateMatcher = new InputErrorStateMatcher(false);

  constructor(private ruleActionService: RuleRoutingActionsService,
              private ChooseTopologyElementsFromMapService: ChooseTopologyElementFromMapService,
              private cd: ChangeDetectorRef,
              private dialog: MatDialog) { }


  ngOnChanges(changes: SimpleChanges): void {
    if (typeof changes.adminState !== 'undefined') {
      const change = changes.adminState;
      if (!change.isFirstChange()) {
        this.changedCombinedList();
      }
    }
  }

  ngOnInit(): void {
    this.initializeData();
  }

  initializeData(): void {
    this.ruleActionService.clearSelection();
    this.normalizationGroup = this.externalData.attributeGroups;
    this.sipManipulationGroups = this.externalData.sipManipulationGroups;

    this.completeNetwork = [...this.externalData.nodes, ...this.externalData.pCons,
      ...this.externalData.voipPeers, ...this.externalData.resourceGroups];
    this.completeNetworkWithOffline = [...this.externalData.nodes, ...this.externalData.offlineNodes,
      ...this.externalData.pCons, ...this.externalData.offlinePCons,
      ...this.externalData.voipPeers, ...this.externalData.offlineVoipPeers, ...this.externalData.resourceGroups];
    this.changedCombinedList();

    this.nodesOnlyWithoutThirdPartyWithOffline = [...this.externalData.nodes,
      ...this.externalData.offlineNodes].filter(node => node.productType !== 'THIRD_PARTY');
    this.nodesOnlyWithoutThirdParty = this.externalData.nodes.filter(node => node.productType !== 'THIRD_PARTY');

    if (this.rule.action && this.rule.action.actions) {
      this.addAdditionalInformationToActions(this.rule.action.actions);
    }
  }

  changedCombinedList(): void {
    this.combinedOptions = this.rule.adminState === 'LOCKED' ? this.completeNetworkWithOffline : this.completeNetwork;
  }

  selectHandler(selected): void {
    this.ruleActionService.setSelectedAction(selected);
  }

  addAction(): void {
    this.ruleActionService.addAction(this.rule.action.actions);
    this.cd.detectChanges();
  }

  addLoopbackAction(): void {
    this.ruleActionService.addLoopbackAction(this.rule.action.actions);
    this.cd.detectChanges();
  }

  addDiscardAction(): void {
    this.ruleActionService.addDiscardAction(this.rule.action.actions);
    this.cd.detectChanges();
  }

  addRegisteredUserAction(): void {
    this.ruleActionService.addRegisteredUserAction(this.rule.action.actions);
    this.cd.detectChanges();
  }

  addContinueAction(): void {
    this.ruleActionService.addContinueAction(this.rule.action.actions);
    this.cd.detectChanges();
  }

  removeAction(index): void {
    this.ruleActionService.removeAction(this.rule.action.actions, index);
  }

  moveUpAction(index): void {
    this.ruleActionService.moveUp(this.rule.action.actions, index);
  }

  moveDownAction(index): void {
    this.ruleActionService.moveDown(this.rule.action.actions, index);
  }

  hasSelection(): boolean {
    return this.ruleActionService.hasSelection();
  }

  isSelected(action): boolean {
    return this.ruleActionService.isSelected(action);
  }

  isFirst(index): boolean {
    return this.ruleActionService.isFirst(this.rule.action.actions, index);
  }

  isLast(index): boolean {
    return this.ruleActionService.isLast(this.rule.action.actions, index);
  }

  isDiscardOrContinueActionSelected(index): boolean {
    return this.ruleActionService.isDiscardOrContinueActionSelected(this.rule.action.actions, index);
  }

  isDiscardOrContinueActionDisable(): boolean {
    return this.ruleActionService.atLeastOneDiscardOrContinueActionExist(this.rule.action.actions);
  }

  isLoopbackActionDisable(): boolean {
    return this.ruleActionService.atLeastOneLoopBackActionActionExist(this.rule.action.actions);
  }

  isRegisteredUserActionDefined(): boolean {
    return this.ruleActionService.isRegisteredUserActionDefined(this.rule.action.actions);
  }

  updateActionMainTitle(value, subAction): void {
    if (subAction.destTypeRequestUri && this.isCombinedRelatedToNode(subAction)) {
      const dialogRef = this.dialog.open(InformationDialogComponent, {
        width: '460px',
        data: {
          message: '“Route based on request URI” in advanced section is not relevant for nodes as destination.'
        }
      });

      dialogRef.componentInstance.onApply.subscribe(() => {
        dialogRef.close();
      });
      subAction.destTypeRequestUri = false;
    }

    this.ruleActionService.updateActionTitle(value, this.rule.action.actions);
  }

  openSelectDialogForSubAction(subAction, selectIndex): void {
    let isOfflineSelected = false;
    const preSelectedItems = {nodes: [], edges: []};

    if (subAction.combined) {
      switch (subAction.combined.elementType) {
        case TopologyElementType.online_node:
          preSelectedItems.nodes.push(TopologyElementPrefix.node + subAction.combined.id);
          break;
        case TopologyElementType.offline_node:
          preSelectedItems.nodes.push(TopologyElementPrefix.node + subAction.combined.id);
          isOfflineSelected = true;
          break;
        case TopologyElementType.online_voip_peer:
          preSelectedItems.nodes.push(TopologyElementPrefix.voip_peer + subAction.combined.id);
          break;
        case TopologyElementType.offline_voip_peer:
          preSelectedItems.nodes.push(TopologyElementPrefix.voip_peer + subAction.combined.id);
          isOfflineSelected = true;
          break;
        case TopologyElementType.online_pcon:
          preSelectedItems.edges.push(TopologyElementPrefix.peer_connection + subAction.combined.id);
          break;
        case TopologyElementType.offline_pcon:
          preSelectedItems.edges.push(TopologyElementPrefix.peer_connection + subAction.combined.id);
          isOfflineSelected = true;
          break;
        default:
          break;
      }
    }

    const selectedItemsObject = {node: [], voipPeer: [], pCon: [], connection: []};
    const selectionConfigurations = this.prepareSelectionForSubActionConfigurations(isOfflineSelected);

    const onOk = () => {
      this.handleTopologyItemsSelection(selectedItemsObject, subAction, selectIndex);
    };

    /*this.sharedEventService.broadcast('openChooseTopologyItemDialog', null, true, selectedItemsObject,
      selectionConfigurations, preSelectedItems, undefined, onOk);*/
  }

  prepareSelectionForSubActionConfigurations(isOfflineSelected): any {
    return {
      withOffline: this.rule.adminState === 'LOCKED',
      isCombinedViewDisplayed: isOfflineSelected,
      multipleSelection: false,
      isSelectionLegal: (selection) => {
        return selection.node.length === 1 || selection.voipPeer.length === 1 ||  selection.pCon.length === 1;
      }
    };
  }

  handleTopologyItemsSelection(selectedItemsObject, subAction, selectIndex): any {
    const networkItems = this.rule.adminState === 'UNLOCKED' ? this.completeNetwork : this.completeNetworkWithOffline;

    if (selectedItemsObject.node && selectedItemsObject.node.length > 0) {
      const node = networkItems.find((item) => {
        return item.id === selectedItemsObject.node[0].dataItem.id &&
          (item.elementType === TopologyElementType.online_node ||
            item.elementType === TopologyElementType.offline_node);
      });
      const allNodes = [...this.externalData.nodes, ...this.externalData.offlineNodes];
      subAction.combined = allNodes.find(n => n.id === node.id);
    } else if (selectedItemsObject.voipPeer && selectedItemsObject.voipPeer.length > 0) {
      const voipPeer = networkItems.find((item) => {
        return item.id === selectedItemsObject.voipPeer[0].dataItem.id &&
          (item.elementType === TopologyElementType.online_voip_peer ||
            item.elementType === TopologyElementType.offline_voip_peer);
      });
      const allVoipPeers = [...this.externalData.voipPeers, ...this.externalData.offlineVoipPeers];
      subAction.combined = allVoipPeers.find(v => v.id === voipPeer.id);
    } else if (selectedItemsObject.pCon && selectedItemsObject.pCon.length > 0) {
      const pcon = networkItems.find((item) => {
        return item.id === selectedItemsObject.pCon[0].dataItem.id &&
          (item.elementType === TopologyElementType.online_pcon ||
            item.elementType === TopologyElementType.offline_pcon);
      });
      const allPcons = [...this.externalData.pCons, ...this.externalData.offlinePCons];
      subAction.combined = allPcons.find(p => p.id === pcon.id);
    }

    if (selectIndex === 0) {
      this.ruleActionService.updateActionTitle(subAction.combined, this.rule.action.actions);
    }
  }

  addNewRuleAction(context): void {
    context.actions.map(item => item.isNormalizationCollapsed = true);
    this.ruleActionService.addRuleAction(context);
  }

  removeRuleAction(context, action): void {
    this.ruleActionService.removeRuleAction(context, action);
  }

  handleBalancingCheckboxChange(context): void {
    this.ruleActionService.updateLoadBalancing(context, context.isEquallyBalanced);
  }

  updateRoutingAttemptsInAllInnerActions(actions): void {
    const newRoutingAttempts = actions[0].routingAttempts;
    actions.forEach(action => action.routingAttempts = newRoutingAttempts);
  }

  isSelectedCombinedIsVoipPeer(action): boolean {
    return action.combined.elementType === 'Online VoIP Peer' || action.combined.elementType === 'Offline VoIP Peer';
  }

  isTopologyGroup(subAction): boolean {
    return subAction && subAction.elementType && subAction.isTopologyGroup;
  }

  isDestTypeRequestUriDisabled(subAction): boolean {
    return subAction.combined && this.isCombinedRelatedToNode(subAction);
  }

  isCustomerNormalizationDisabled(subAction): boolean {
    return !this.rule.destination.allCustomers &&
        !(_.isArray(this.rule.destination.destCustomers) &&
            this.rule.destination.destCustomers.length > 0) &&
         !subAction.customerNormalizationSIPHeader;
  }

  isCombinedRelatedToNode(subAction): boolean {
    return subAction.combined.elementType === TopologyElementType.online_node ||
      subAction.combined.elementType === TopologyElementType.offline_node ||
      subAction.combined.elementType === 'Nodes group';
  }


  resourceAttemptsMaxValue(item): number {
    return item.combined && Number.isInteger(item.combined.numberOfElements)
      ? item.combined.numberOfElements : 1;
  }

  addAdditionalInformationToActions(actions): void {
    actions.forEach(action => {
      action.actions.forEach(item => {
        if (item && item.attemptsInTopologyGroup &&
          item.combined && item.combined.numberOfElements) {
          if (item.attemptsInTopologyGroup > item.combined.numberOfElements) {
            item.resourceAttemptsError = `Note, the resource attempt [${item.attemptsInTopologyGroup}]` +
              ` is higher than the number of entities in the group.<br />` +
              `Pressing OK will change the actual value to the maximal of two.`;
          } else{
            item.resourceAttemptsError = '';
          }
            }
      });
    });
  }

  onMapClick($event: any): void {
    if (!_.isNil($event.node) && $event.node.length > 0) {
      const selectedNodes = $event.node.map(nodeItem => nodeItem.dataItem.id);
      const filteredNode = this.combinedOptions.find(item => item.id === selectedNodes[0] &&
        (item.elementType === 'Online Node' || item.elementType === 'Offline Node'));

      this.focusedSubAction.combined = filteredNode;
    } else if (!_.isNil($event.pCon) && $event.pCon.length > 0) {
      const selectedPCons = $event.pCon.map(pConItem => pConItem.dataItem.id);
      const filteredPCon = this.combinedOptions.find(item => item.id === selectedPCons[0] &&
        (item.elementType === 'Online Peer Connection' || item.elementType === 'Offline Peer Connection'));

      this.focusedSubAction.combined = filteredPCon;
    } else if (!_.isNil($event.voipPeer) && $event.voipPeer.length > 0) {
      const selectedVoipPeer = $event.voipPeer.map(voipPeerItem => voipPeerItem.dataItem.id);
      const filteredVoipPeer = this.combinedOptions.find(item => item.id === selectedVoipPeer[0]
        && (item.elementType === 'Online VoIP Peer' || item.elementType === 'Offline VoIP Peer'));

      this.focusedSubAction.combined = filteredVoipPeer;
    }

    this.updateActionMainTitle(this.focusedSubAction.combined, this.focusedSubAction);
  }

  onSipReasonChange($event: MatRadioChange): void {

  }

  mapFocus(action, subAction): void {
    this.focusedSubAction = subAction;

    this.updateMap(subAction);
  }

  updateMap(subAction = null): void {
    const selectedNodes = [];
    const selectedPCons = [];
    const selectedVPeers = [];
    const withOffline = this.adminState === 'LOCKED';
    const isSelectionLegal = () => {
      return true;
    };

    if (subAction === this.focusedSubAction) {
      if (subAction.combined && (subAction.combined.elementType === 'Online Node' ||
        subAction.combined.elementType === 'Offline Node')) {
        selectedNodes.push(subAction.combined.id);
      } else if (subAction.combined && (subAction.combined.elementType === 'Online Peer Connection' ||
        subAction.combined.elementType === 'Offline Peer Connection')) {
        selectedPCons.push(subAction.combined.id);
      } else if (subAction.combined && subAction.combined.elementType === 'Online VoIP Peer') {
        selectedVPeers.push(subAction.combined.id);
      }

      this.mapOptions = this.ChooseTopologyElementsFromMapService.prepareOptionsForNodesPConsAndVoipPeer(isSelectionLegal, withOffline,
        selectedNodes, selectedPCons, selectedVPeers, this.externalData.nodes,
        this.externalData.pCons, this.externalData.voipPeers);
    }
  }

  isEquallyBalancedValid(): void {
    if (_.isArray(this.rule.action.actions) && this.rule.action.actions.length > 0) {
      for (let i = 0; i < this.rule.action.actions.length; i++) {
        if (this.actionItemHasInvalidPercentage(this.rule.action.actions[i].actions)) {
          this.errorStateMatcher = new InputErrorStateMatcher(true);
          return;
        }
      }
    }

    this.errorStateMatcher = new InputErrorStateMatcher(false);
  }

  private actionItemHasInvalidPercentage(actionItem): boolean {
    let percentageSummary = 0;
    let haveZeroPercentage = false;

    actionItem.forEach((item) => {
      percentageSummary =  percentageSummary + item.percentage;
      haveZeroPercentage = item.percentage <= 0 ? true : haveZeroPercentage;
    });
    return  actionItem.length === 0 || haveZeroPercentage || (percentageSummary !== 100 && !actionItem[0].isEquallyBalanced);
  }

  private setMaxResourceAttemptsValue(subAction): void {
    if (subAction.combined && this.isTopologyGroup(subAction.combined)) {
      subAction.attemptsInTopologyGroup = this.resourceAttemptsMaxValue(subAction);
    }
  }
}
